"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Menu, X, Phone, Mail } from "lucide-react";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium text-foreground transition-colors hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] after:ease-[var(--ease-smooth)] hover:after:w-full focus:after:w-full outline-none">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [open, setOpen] = useState(false);
  if (loading) {
    return <div className="h-9 w-9 bg-muted animate-pulse rounded-md" />;
  }
  return <SafeHydrate><Drawer direction="right" open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button className="inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground hover:bg-muted/50 focus:bg-muted/50 transition-colors outline-none focus-ring" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="flex items-center justify-between border-b border-border pb-4">
          <DrawerTitle>Menu</DrawerTitle>
          <DrawerClose asChild>
            <button className="inline-flex items-center justify-center h-8 w-8 rounded-md text-foreground hover:bg-muted/50 focus:bg-muted/50 transition-colors outline-none focus-ring" aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
          </DrawerClose>
        </DrawerHeader>
        <nav className="flex flex-col gap-1 p-4">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} onClick={() => setOpen(false)} className="relative flex items-center px-4 py-3 text-sm font-medium text-foreground rounded-md transition-colors hover:bg-muted/50 focus:bg-muted/50 after:absolute after:bottom-0 after:left-4 after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] after:ease-[var(--ease-smooth)] hover:after:w-[calc(100%-2rem)] focus:after:w-[calc(100%-2rem)] outline-none">
              {item.label}
            </Link>)}
        </nav>
        <div className="border-t border-border p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">Contact Tim Bernstein</p>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="tel:517-250-3998" className="flex items-center gap-2 hover:text-primary-dark transition-colors">
                <Phone className="h-4 w-4" />
                <span>517-250-3998</span>
              </Link>
              <Link to="mailto:Service@bbvets.com" className="flex items-center gap-2 hover:text-primary-dark transition-colors">
                <Mail className="h-4 w-4" />
                <span>Service@bbvets.com</span>
              </Link>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">Contact Stephen Bosley</p>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="tel:517-960-3813" className="flex items-center gap-2 hover:text-primary-dark transition-colors">
                <Phone className="h-4 w-4" />
                <span>517-960-3813</span>
              </Link>
              <Link to="mailto:Boz@bbvets.com" className="flex items-center gap-2 hover:text-primary-dark transition-colors">
                <Mail className="h-4 w-4" />
                <span>Boz@bbvets.com</span>
              </Link>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer></SafeHydrate>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${scrolled ? "py-3 shadow-[var(--shadow-soft)] bg-background/95 backdrop-blur-sm" : "py-4"}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between gap-4">
          <Link to="/#" className="flex items-center gap-3 flex-shrink-0">
            <div className="block">
              <div className="text-lg font-bold text-foreground leading-tight">B&B VETERANS LLC</div>
              <div className="text-xs text-muted-foreground leading-tight mt-0.5">THE NAME YOU TRUST, THE QUALITY YOU DESERVE.</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex gap-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-foreground">Tim Bernstein</span>
                <Link to="tel:517-250-3998" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary-dark transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  <span>517-250-3998</span>
                </Link>
                <Link to="mailto:Service@bbvets.com" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary-dark transition-colors">
                  <Mail className="h-3.5 w-3.5" />
                  <span>Service@bbvets.com</span>
                </Link>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-foreground">Stephen Bosley</span>
                <Link to="tel:517-960-3813" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary-dark transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  <span>517-960-3813</span>
                </Link>
                <Link to="mailto:Boz@bbvets.com" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary-dark transition-colors">
                  <Mail className="h-3.5 w-3.5" />
                  <span>Boz@bbvets.com</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="hidden md:block lg:hidden">
            <MenuProvider menu_id="3">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="md:hidden">
            <MenuProvider menu_id="3">
              <MobileMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        <div className="hidden lg:flex justify-end mt-3">
          <MenuProvider menu_id="3">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}